// src/modules/app-views/invoices/pages/Document/index.jsx
// *******************************************************************************
// INVOICE DOCUMENT PAGE
// *******************************************************************************
// Main page for creating and editing fiscal invoices (NFe, NFCe, NFSe).
// Contains three cards: Header (customer/type), Items, and Totals/Payments.
// *******************************************************************************

// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Tabs, App, Form, Input, message, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { SimpleFormProvider } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleFormProviderInitializer from 'components/SimpleComponents/SimpleFormProviderInitializer';
import SimpleLoadingArea from 'components/SimpleComponents/SimpleLoadingArea';
import CardHeaderWithCountry from 'layouts/Pages/PageHeader/CardHeaderWithCountry';
import useFormInstance from 'hooks/useFormInstance';
import useZustand from 'hooks/useZustand';
import translateX from 'utils/translateX';
import { returnOnlyNumbers } from 'utils/helpers';
import appService from 'services/appService';
import axiosService from 'services/axiosService';
import financialService from 'services/financialService';
import fiscalService from 'services/fiscalService';
import invoicesService from 'modules/app-views/invoices/service';
import { API } from 'constants/Api';
import InvoicesRecordTab from './tabs/RecordTab';
import PreviewTab from './tabs/PreviewTab';
import AttachmentsTab from './tabs/AttachmentsTab';
import ActionsFloatButton from 'components/JarvislyModule/ActionsFloatButton';
import invoicesDocumentFloatButton from './actions/floatButton';
import showEmitInvoiceNfceModal from '../Init/modals/EmitInvoiceNfceModal';
import showPreviewNFCeModal from '../Init/modals/PreviewNFCeModal';
import showCancelNfceModal from 'modules/app-views/fiscalConfig/pages/Document/tabs/NfceTab/modals/CancelNfceModal';
import showCorrectionLetterModal from 'modules/app-views/fiscalConfig/pages/Document/tabs/NfceTab/modals/CorrectionLetterModal';
import FiscalBootstrapModal, { needsFiscalBootstrap } from '../Init/modals/FiscalBootstrapModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const InvoicesDocument = props => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { tab, initTab, handleTabChange, availableTabs, buildTabs, data, viewMode, refresh, registerTabFieldPrefixes } =
    useDocumentContext();

  const storedCompany = useZustand('auth', 'company');
  const storedSubscription = useZustand('auth', 'subscription');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const navigate = useNavigate();
  const { upsert } = useZustand('global', 'useApi');
  const { modal } = App.useApp();

  // Map incoming referencedDocuments (Backend) to referenceNfe (Frontend UI object)
  const mappedData = useMemo(() => {
    if (!data) return data;
    const newData = { ...data };
    if (newData.referencedDocuments && newData.referencedDocuments.length > 0) {
      const activeRef = newData.referencedDocuments[0];
      newData.referenceNfe = {
        accessKey: activeRef.accessKey,
        documentNumber: activeRef.documentNumber,
      };
    }
    return newData;
  }, [data]);

  const { formRef, form, isInitializedRef } = useFormInstance({ data: mappedData });

  // Subscribe to language changes to re-render tabs when language changes
  const storedLanguage = useZustand('theme', 'language');

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const requiredFields = [
    'consumerPresence',
    'purpose',
    'natureOfOperation',
    'issueDate',
    'intermediation',
    'freightModality',
  ];
  const isNew = !data?._id || data?._id === 'add';
  const invoiceStatus = data?.status;
  const isAuthorized = ['authorized', 'canceled'].includes(invoiceStatus);

  // Determine default document type based on subscription resources
  // If subscription has 'moduleresources_nfe' → default to 'nfe', otherwise 'nfce'
  const subscriptionResources = storedSubscription?.resources || [];
  const hasNfeResource = subscriptionResources.includes('moduleresources_nfe');
  const defaultDocumentType = hasNfeResource ? 'nfe' : 'nfce';

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [items, setItems] = useState([]);
  const [payments, setPayments] = useState([]);
  const [issuer, setIssuer] = useState(null);

  // Email/WhatsApp modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [whatsappModalOpen, setWhatsappModalOpen] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  const [isEmitting, setIsEmitting] = useState(false);
  const [emailForm] = Form.useForm();
  const [whatsappForm] = Form.useForm();

  // Fiscal bootstrap modal state - shows when company needs fiscal configuration
  const [showFiscalBootstrap, setShowFiscalBootstrap] = useState(() => {
    return needsFiscalBootstrap(storedCompany, storedSubscription);
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!initTab) return;
    registerTabFieldPrefixes?.({ '': initTab });
  }, [registerTabFieldPrefixes, initTab]);

  // Initialize or reset items and payments when data changes (new document or editing)
  useEffect(() => {
    setItems(data?.items || []);
    setPayments(data?.payment?.methods || []);
  }, [data]);

  // Check if fiscal bootstrap modal needs to be shown when company/subscription changes
  useEffect(() => {
    const needsBootstrap = needsFiscalBootstrap(storedCompany, storedSubscription);
    setShowFiscalBootstrap(needsBootstrap);
  }, [storedCompany, storedSubscription]);

  // Initialize issuer: for new invoices use storedCompany, for existing use issuerEntity
  useEffect(() => {
    if (isNew) {
      // New invoice: use logged company as issuer
      if (storedCompany) {
        // Extract CNPJ value (can be object or string)
        const cnpjValue =
          typeof storedCompany.cnpj === 'object' ? storedCompany.cnpj?.value : storedCompany.cnpj;

        // Extract IE (can be in ie field or fiscalData.stateRegistration)
        const ieValue = storedCompany.ie || storedCompany.fiscalData?.stateRegistration;

        // Extract UF (can be in address.region or address.state)
        const ufValue = storedCompany.address?.region || storedCompany.address?.state;

        // Extract CRT (taxRegime to CRT code)
        const taxRegime = storedCompany.fiscalData?.taxRegime;
        const crtValue = convertTaxRegimeToCrt(taxRegime);

        const issuerData = {
          entityId: storedCompany._id,
          cnpj: cnpjValue,
          razaoSocial: storedCompany.razaoSocial || storedCompany.name,
          nomeFantasia: storedCompany.nomeFantasia,
          ie: ieValue,
          uf: ufValue,
          cityCode: storedCompany.address?.cityCode,
          crt: crtValue,
          fiscalData: storedCompany.fiscalData,
        };

        setIssuer(issuerData);
      }
    } else {
      // Editing: use issuerEntity from backend (hydrated) or fallback to issuer snapshot
      if (data?.issuerEntity) {
        // Extract CNPJ value (can be object or string)
        const cnpjValue =
          typeof data.issuerEntity.cnpj === 'object'
            ? data.issuerEntity.cnpj?.value
            : data.issuerEntity.cnpj;

        // Extract IE (can be in ie field or fiscalData.stateRegistration)
        const ieValue = data.issuerEntity.ie || data.issuerEntity.fiscalData?.stateRegistration;

        // Extract UF (can be in address.region or address.state)
        const ufValue = data.issuerEntity.address?.region || data.issuerEntity.address?.state;

        // Extract CRT (taxRegime to CRT code)
        const taxRegime = data.issuerEntity.fiscalData?.taxRegime;
        const crtValue = convertTaxRegimeToCrt(taxRegime);

        const issuerData = {
          entityId: data.issuerEntity._id,
          cnpj: cnpjValue,
          razaoSocial: data.issuerEntity.razaoSocial || data.issuerEntity.name,
          nomeFantasia: data.issuerEntity.nomeFantasia,
          ie: ieValue,
          uf: ufValue,
          cityCode: data.issuerEntity.address?.cityCode,
          crt: crtValue,
          fiscalData: data.issuerEntity.fiscalData,
        };

        setIssuer(issuerData);
      } else if (data?.issuer) {
        // Fallback to saved issuer snapshot
        setIssuer(data.issuer);
      }
    }
  }, [isNew, storedCompany, data?.issuerEntity, data?.issuer]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = useCallback(
    async ({ values, allValues }) => {
      // Use allValues for reading form state (contains ALL form values)
      // Use values only for detecting what changed (contains only CHANGED values)
      const invoiceId = allValues?._id || data?._id;
      // Use form value, then data, then defaultDocumentType (based on subscription)
      const documentType = allValues?.documentType || data?.documentType || defaultDocumentType;
      const isNfce = documentType === 'nfce';

      // Get recipientId from form or fallback to data (editing existing invoice)
      const recipientId = allValues?.recipientId || data?.recipientId;

      // ─────────────────────────────────────────────────────────────────────────
      // VALIDATIONS
      // ─────────────────────────────────────────────────────────────────────────

      // Validate items - at least one item required
      if (!items || items.length === 0) {
        appService.message({
          flag: 'error',
          text: 'invoice_validation_no_items',
        });
        return false;
      }

      // Validate recipient for NFe (required) - optional for NFCe
      if (!isNfce && !recipientId) {
        appService.message({
          flag: 'error',
          text: 'invoice_validation_no_recipient',
        });
        return false;
      }

      // Return, complementary and adjustment (storno) invoices do not have their own payment
      // (the payment belongs to the referenced original invoice)
      const isReturnOrComplementary = ['return', 'complementary', 'adjustment'].includes(
        allValues?.purpose || data?.purpose
      );

      // Validate payments - at least one payment method required (skip for return/complementary)
      if (!isReturnOrComplementary && (!payments || payments.length === 0)) {
        appService.message({
          flag: 'error',
          text: 'invoice_validation_no_payments',
        });
        return false;
      }

      // ─────────────────────────────────────────────────────────────────────────
      // CALCULATE TOTALS
      // ─────────────────────────────────────────────────────────────────────────
      const invoiceTotals = financialService.calculateInvoiceTotals(items);
      const taxTotals = fiscalService.calculateInvoiceTaxes(items);

      // Calculate payments total and change (rounded to avoid floating point issues)
      const paymentsTotal = financialService.roundCurrency(
        payments.reduce((sum, p) => sum + (p.amount || 0), 0)
      );
      const changeValue = financialService.calculateChange(
        invoiceTotals.invoiceTotal,
        paymentsTotal
      );

      // Validate payment amount covers invoice total (skip for return/complementary)
      if (!isReturnOrComplementary && paymentsTotal < financialService.roundCurrency(invoiceTotals.invoiceTotal)) {
        appService.message({
          flag: 'error',
          text: 'invoice_validation_insufficient_payment',
        });
        return false;
      }

      // ─────────────────────────────────────────────────────────────────────────
      // GET SERIE FROM COMPANY FISCAL DATA
      // ─────────────────────────────────────────────────────────────────────────
      const serie = isNfce
        ? storedCompany?.fiscalData?.nfce?.serie || 1
        : storedCompany?.fiscalData?.nfe?.serie || 1;

      // Get environment from form or company settings
      const environment =
        allValues?.environment ??
        (isNfce
          ? storedCompany?.fiscalData?.nfce?.environment
          : storedCompany?.fiscalData?.nfe?.environment) ??
        storedCompany?.fiscalData?.defaultEnvironment ??
        'homologation';

      // ─────────────────────────────────────────────────────────────────────────
      // BUILD DATA TO SAVE (matching backend schema field names)
      // All fields use string/boolean values - conversion to SEFAZ codes happens at emission
      // Use allValues (complete form state) instead of values (only changed fields)
      // ─────────────────────────────────────────────────────────────────────────
      const dataToSave = {
        // Form values (documentType, operationType, etc.)
        ...allValues,

        // NFC-e is always exit (saida) per Brazilian legislation - override any stored value
        ...(isNfce && { operationType: 'exit' }),

        // Document identification
        documentType,
        documentSerie: serie,
        environment, // 'production' | 'homologation'

        // Operation details
        // Preserve existing DB value as fallback to avoid overwriting estorno/return/complementary natOp
        operationNature: allValues?.natureOfOperation || data?.operationNature || 'Venda',
        isFinalConsumer: allValues?.finalConsumer === true, // boolean

        // Issuer data - ensure it's always complete with all required fields
        issuer: buildIssuerData({ issuer, storedCompany, data }),

        // Recipient data: deep merge to preserve fields without Form.Item (e.g. address)
        // allValues.recipient only contains fields registered as Form.Items (shallow form collection)
        // data.recipient has the full snapshot from the database (including address, etc.)
        recipient: { ...(data?.recipient || {}), ...(allValues?.recipient || {}) },
        recipientId,

        // Items with proper structure (cleaned for backend schema)
        items: cleanItemsForBackend(items),

        // Calculated totals (schema uses "Amount" suffix)
        totals: {
          productsAmount: invoiceTotals.productsTotal,
          servicesAmount: 0, // Future: add services support
          discountAmount: invoiceTotals.discount,
          freightAmount: invoiceTotals.freight,
          insuranceAmount: invoiceTotals.insurance,
          otherExpensesAmount: invoiceTotals.otherExpenses,
          icmsBaseAmount: taxTotals.icmsBase,
          icmsAmount: taxTotals.icmsValue,
          pisAmount: taxTotals.pisValue,
          cofinsAmount: taxTotals.cofinsValue,
          ipiAmount: taxTotals.ipiValue,
          documentAmount: invoiceTotals.invoiceTotal,
        },

        // Payment data (includes change when payments exceed invoice total)
        payment: {
          indicator: allValues?.payment?.indicator ?? 0,
          methods: payments.map(p => ({
            code: p.code,
            type: p.type,
            amount: financialService.roundCurrency(p.amount || 0),
            description: p.description || '',
          })),
          // Change (troco) - SEFAZ requires this when payment exceeds document total
          change: changeValue > 0 ? financialService.roundCurrency(changeValue) : 0,
        },

        // Logistics: merge to preserve vehicle/volumes from nested fields
        logistics: {
          ...(allValues?.logistics || {}),
          freightModeCode: allValues?.freightModality ?? 'noFreight',
          carrier: allValues?.carrier || data?.logistics?.carrier,
        },
      };

      // Clean up form-only fields moved into logistics
      delete dataToSave.carrierId;
      delete dataToSave.carrier;

      // Handle referenced documents (Return, Complementary)
      if (allValues?.referenceNfe && (allValues.referenceNfe.accessKey || allValues.referenceNfe.documentNumber)) {
        dataToSave.referencedDocuments = [{
          accessKey: allValues.referenceNfe.accessKey,
          documentNumber: allValues.referenceNfe.documentNumber
        }];
        delete dataToSave.referenceNfe;
      }

      // SAVE
      await upsert({ dataSet: dataToSave, _id: invoiceId });
      return true;
    },
    [data, defaultDocumentType, issuer, items, payments, storedCompany, upsert]
  );

  const handleAddItem = useCallback(newItem => {
    setItems(prev => [...prev, { ...newItem, itemNumber: prev.length + 1 }]);
  }, []);

  const handleRemoveItem = useCallback(index => {
    setItems(prev =>
      prev
        .filter((_, i) => i !== index)
        .map((item, i) => ({
          ...item,
          itemNumber: i + 1,
        }))
    );
  }, []);

  const handleAddPayment = useCallback(newPayment => {
    setPayments(prev => [...prev, newPayment]);
  }, []);

  const handleRemovePayment = useCallback(index => {
    setPayments(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Invoice fiscal actions handlers
  const handleEmitInvoice = useCallback(async () => {
    const invoiceId = data?._id;
    // Use data documentType, then defaultDocumentType (based on subscription)
    const documentType = data?.documentType?.toLowerCase() || defaultDocumentType;
    const isNfce = documentType === 'nfce';
    const isFinalConsumer = data?.isFinalConsumer || data?.finalConsumer;

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 0: VALIDATE COMPANY ELIGIBILITY
    // ─────────────────────────────────────────────────────────────────────────
    const validation = await invoicesService.validateEmissionEligibility({
      invoice: data,
    });

    if (!validation.valid) {
      // Notification already shown by validateEmissionEligibility
      return;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // EXECUTE EMISSION - Main emission process with single updating message
    // ─────────────────────────────────────────────────────────────────────────
    const executeEmission = async (cpf = null) => {
      // Lock form during emission
      setIsEmitting(true);

      // Single message that updates throughout the process
      const msg = await appService.message({
        flag: 'loading',
        text: 'emitting_fiscal_document_wait',
        duration: 0, // Keep visible until we update it
      });

      try {
        // ─────────────────────────────────────────────────────────────────────
        // PROCESS 1: SAVE THE DOCUMENT SILENTLY
        // ─────────────────────────────────────────────────────────────────────
        try {
          // Validate and get form values
          const allValues = await form.validateFields();

          // Use form value, then data, then defaultDocumentType (based on subscription)
          const docType = allValues?.documentType || data?.documentType || defaultDocumentType;
          const docIsNfce = docType === 'nfce';

          // Get recipientId from form
          const recipientId = allValues?.recipientId || data?.recipientId;

          // Validate items
          if (!items || items.length === 0) {
            msg.update({ flag: 'error', text: 'invoice_validation_no_items', duration: 4 });
            setIsEmitting(false);
            return;
          }

          // Validate recipient for NFe
          if (!docIsNfce && !recipientId) {
            msg.update({ flag: 'error', text: 'invoice_validation_no_recipient', duration: 4 });
            setIsEmitting(false);
            return;
          }

          // Return and complementary invoices do not have their own payment
          // (the payment belongs to the referenced original invoice)
          // Use form value first (allValues) to handle unsaved changes, fallback to data
          const isReturnOrComplementary = ['return', 'complementary', 'adjustment'].includes(
            allValues?.purpose || data?.purpose
          );

          // Validate payments (skip for return/complementary)
          if (!isReturnOrComplementary && (!payments || payments.length === 0)) {
            msg.update({ flag: 'error', text: 'invoice_validation_no_payments', duration: 4 });
            setIsEmitting(false);
            return;
          }

          // Calculate totals
          const invoiceTotals = financialService.calculateInvoiceTotals(items);
          const taxTotals = fiscalService.calculateInvoiceTaxes(items);
          const paymentsTotal = financialService.roundCurrency(
            payments.reduce((sum, p) => sum + (p.amount || 0), 0)
          );
          // Calculate change (troco) - positive when payments exceed invoice total
          const changeValue = financialService.calculateChange(
            invoiceTotals.invoiceTotal,
            paymentsTotal
          );

          // Validate payment amount (skip for return/complementary)
          if (!isReturnOrComplementary && paymentsTotal < financialService.roundCurrency(invoiceTotals.invoiceTotal)) {
            msg.update({
              flag: 'error',
              text: 'invoice_validation_insufficient_payment',
              duration: 4,
            });
            setIsEmitting(false);
            return;
          }

          // Get serie from company fiscal data
          const serie = docIsNfce
            ? storedCompany?.fiscalData?.nfce?.serie || 1
            : storedCompany?.fiscalData?.nfe?.serie || 1;

          // Priority: form value → 'production' default.
          // fiscalData.nfe.environment and defaultEnvironment are Mongoose schema defaults
          // that always equal 'homologation' (not an explicit user choice) — do not use them.
          const environment = allValues?.environment ?? 'production';

          // Build data to save
          const dataToSave = {
            ...allValues,
            documentType: docType,
            documentSerie: serie,
            environment,
            // Preserve existing DB value as fallback to avoid overwriting estorno/return/complementary natOp
            operationNature: allValues?.natureOfOperation || data?.operationNature || 'Venda',
            isFinalConsumer: allValues?.finalConsumer === true,
            issuer: buildIssuerData({ issuer, storedCompany, data }),
            // Deep merge: preserve fields without Form.Item (e.g. address) from saved data
            recipient: { ...(data?.recipient || {}), ...(allValues?.recipient || {}) },
            recipientId,
            items: cleanItemsForBackend(items),
            totals: {
              productsAmount: invoiceTotals.productsTotal,
              servicesAmount: 0,
              discountAmount: invoiceTotals.discount,
              freightAmount: invoiceTotals.freight,
              insuranceAmount: invoiceTotals.insurance,
              otherExpensesAmount: invoiceTotals.otherExpenses,
              icmsBaseAmount: taxTotals.icmsBase,
              icmsAmount: taxTotals.icmsValue,
              pisAmount: taxTotals.pisValue,
              cofinsAmount: taxTotals.cofinsValue,
              ipiAmount: taxTotals.ipiValue,
              documentAmount: invoiceTotals.invoiceTotal,
            },
            payment: {
              indicator: allValues?.payment?.indicator ?? 0,
              methods: payments.map(p => ({
                code: p.code,
                type: p.type,
                amount: financialService.roundCurrency(p.amount || 0),
                description: p.description || '',
              })),
              // Change (troco) - SEFAZ requires this when payment exceeds document total
              change: changeValue > 0 ? financialService.roundCurrency(changeValue) : 0,
            },
            logistics: {
              ...(allValues?.logistics || {}),
              freightModeCode: allValues?.freightModality ?? 'noFreight',
              carrier: allValues?.carrier || data?.logistics?.carrier,
            },
          };

          delete dataToSave.carrierId;
          delete dataToSave.carrier;

          // Handle referenced documents (Return, Complementary)
          if (allValues?.referenceNfe && (allValues.referenceNfe.accessKey || allValues.referenceNfe.documentNumber)) {
            dataToSave.referencedDocuments = [{
              accessKey: allValues.referenceNfe.accessKey,
              documentNumber: allValues.referenceNfe.documentNumber
            }];
            delete dataToSave.referenceNfe;
          }

          // Save the document silently (using axiosService.put directly)
          await axiosService.put({
            url: API.INVOICES.URL.replace(':_id?', invoiceId),
            data: dataToSave,
          });
        } catch (saveError) {
          if (saveError?.errorFields?.length > 0) {
            const firstErrorField = saveError.errorFields[0]?.name;
            if (firstErrorField) form.scrollToField(firstErrorField, { behavior: 'smooth', block: 'center' });
            msg.update({ flag: 'error', text: 'invoice_validation_fill_required_fields', duration: 5 });
          } else {
            msg.update({ flag: 'error', text: 'save_failed', duration: 4 });
          }

          setIsEmitting(false);
          return;
        }

        // Small delay to ensure save is reflected
        await new Promise(resolve => setTimeout(resolve, 300));
        try {
          const validateResponse = await axiosService.post({
            url: API.INVOICES.VALIDATE,
            data: {
              ids: [invoiceId],
              level: 'internal',
              updateDocuments: true,
            },
          });

          const [validateData] = validateResponse || [];
          const detail = validateData?.details?.[0];

          if (!detail?.isValid) {
            // Validation failed - update message and refresh to show errors
            msg.update({
              flag: 'error',
              text: 'validation_failed',
              duration: 4,
            });

            // Refresh to show updated validation status
            if (typeof refresh === 'function') {
              refresh();
            }

            setIsEmitting(false);
            return;
          }
        } catch (validateError) {
          msg.update({
            flag: 'error',
            text: 'validation_failed',
            duration: 4,
          });

          if (typeof refresh === 'function') {
            refresh();
          }

          setIsEmitting(false);
          return;
        }

        // ─────────────────────────────────────────────────────────────────────
        // PROCESS 3: EMIT THE DOCUMENT TO SEFAZ
        // Get documentType from form (same value used in PROCESS 1 to save)
        // ─────────────────────────────────────────────────────────────────────
        try {
          // Get documentType from form - this is the value that was saved in PROCESS 1
          const emitDocType = (
            form.getFieldValue('documentType') ||
            data?.documentType ||
            'nfce'
          ).toUpperCase();

          // Read environment from form — frontend form is the source of truth.
          // Do NOT fall back to data?.environment: that MongoDB value may be stale 'homologation'
          // saved from schema defaults. Always prefer 'production' if form has no value.
          const emitEnvironment = form.getFieldValue('environment') || 'production';

          const emitResult = await invoicesService.emit(invoiceId, emitDocType, {
            cpf: cpf || null,
            environment: emitEnvironment,
          });

          if (emitResult?.success) {
            // Emission successful
            msg.update({
              flag: 'success',
              text: 'invoice_emitted_successfully',
              duration: 3,
            });

            // Refresh document to show updated status
            if (typeof refresh === 'function') {
              refresh();
            }
          } else {
            // Emission failed - show error
            msg.update({
              flag: 'error',
              text: 'sefaz_emission_failed',
              duration: 4,
            });

            // Show notification with detailed error
            const errorMessage = emitResult?.message || emitResult?.error || 'unknown_error';
            appService.notification({
              flag: 'error',
              title: 'sefaz_emission_failed',
              description: errorMessage,
              noTranslateDescription: true,
            });

            // Refresh document to show updated status
            if (typeof refresh === 'function') {
              refresh();
            }
          }
        } catch (emitError) {
          msg.update({
            flag: 'error',
            text: 'sefaz_emission_failed',
            duration: 4,
          });

          // Show notification with detailed error
          const errorData = emitError?.response?.data?.data || emitError?.response?.data || {};
          const errorMessage =
            errorData?.providerMessage ||
            errorData?.description ||
            errorData?.message ||
            emitError?.message ||
            'unknown_error';

          appService.notification({
            flag: 'error',
            title: 'sefaz_emission_failed',
            description: errorMessage,
            noTranslateDescription: true,
          });

          // Refresh document to show updated status
          if (typeof refresh === 'function') {
            refresh();
          }
        }
      } finally {
        // Unlock form after emission completes (success or error)
        setIsEmitting(false);
      }
    };

    // ─────────────────────────────────────────────────────────────────────────
    // PROCESS 1: CHECK IF NFCe FOR FINAL CONSUMER - OPEN CPF MODAL FIRST
    // ─────────────────────────────────────────────────────────────────────────
    if (isNfce && isFinalConsumer) {
      // Check if CPF is already filled in the recipient data (check cpfCnpj first, then cpf)
      const existingCpf =
        data?.recipient?.cpfCnpj ||
        form.getFieldValue(['recipient', 'cpfCnpj']) ||
        data?.recipient?.cpf ||
        form.getFieldValue(['recipient', 'cpf']);
      const cleanExistingCpf = existingCpf ? returnOnlyNumbers(existingCpf) : null;

      // If CPF already exists, skip the modal and emit directly
      if (cleanExistingCpf && cleanExistingCpf.length === 11) {
        executeEmission(cleanExistingCpf);
        return;
      }

      // Open modal to collect CPF, then execute emission
      showEmitInvoiceNfceModal({
        invoiceData: data,
        onCpfCollected: cpf => {
          // After CPF is collected (or skipped), execute emission
          executeEmission(cpf);
        },
      });
      return;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // PROCESS 2: NFe OR NFCe WITHOUT FINAL CONSUMER - EMIT DIRECTLY
    // ─────────────────────────────────────────────────────────────────────────
    await executeEmission();
  }, [
    data,
    defaultDocumentType,
    invoiceStatus,
    refresh,
    form,
    items,
    payments,
    storedCompany,
    issuer,
  ]);

  const handleDownloadPdf = useCallback(() => {
    appService.message({ flag: 'info', text: 'download_pdf_clicked' });
  }, [data?._id, data?.accessKey]);

  const handleDownloadXml = useCallback(() => {
    appService.message({ flag: 'info', text: 'download_xml_clicked' });
  }, [data?._id, data?.accessKey]);

  // EMAIL - Open modal to send invoice by email
  const handleOpenEmailModal = useCallback(() => {
    const recipient = data?.recipient || data?.recipientEntity || {};
    const recipientEmail = recipient.email || '';

    emailForm.setFieldsValue({
      to: recipientEmail,
      subject: `${translateX('fiscal_document')} - ${data?.documentNumber || ''}`,
      message: translateX('email_default_message'),
    });
    setEmailModalOpen(true);
  }, [emailForm, data?.recipient, data?.recipientEntity, data?.documentNumber]);

  const handleSendEmail = useCallback(async () => {
    try {
      const values = await emailForm.validateFields();
      setSendingEmail(true);

      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));

      message.success(translateX('email_sent_success'));
      setEmailModalOpen(false);
      emailForm.resetFields();
    } catch (error) {
      if (error?.errorFields) return; // Form validation error
      message.error(translateX('email_send_error'));
    } finally {
      setSendingEmail(false);
    }
  }, [emailForm, data?._id]);

  // WHATSAPP - Open modal to send invoice by WhatsApp
  const handleOpenWhatsappModal = useCallback(() => {
    const recipient = data?.recipient || data?.recipientEntity || {};
    const recipientPhone = recipient.phone || recipient.mobile || '';

    whatsappForm.setFieldsValue({
      phone: recipientPhone,
      message: translateX('whatsapp_default_message'),
    });
    setWhatsappModalOpen(true);
  }, [whatsappForm, data?.recipient, data?.recipientEntity]);

  const handleSendWhatsapp = useCallback(async () => {
    try {
      const values = await whatsappForm.validateFields();
      setSendingWhatsapp(true);

      // Open WhatsApp Web with pre-filled message
      const phone = values.phone.replace(/\D/g, '');
      const text = encodeURIComponent(values.message);
      const whatsappUrl = `https://wa.me/55${phone}?text=${text}`;

      window.open(whatsappUrl, '_blank');

      message.success(translateX('whatsapp_opened'));
      setWhatsappModalOpen(false);
      whatsappForm.resetFields();
    } catch (error) {
      if (error?.errorFields) return; // Form validation error
      message.error(translateX('whatsapp_send_error'));
    } finally {
      setSendingWhatsapp(false);
    }
  }, [whatsappForm]);

  const handleCancelInvoice = useCallback(() => {
    const invoiceId = data?._id;
    const invoiceNumber = data?.documentNumber;
    const currentStatus = data?.status;

    // Only authorized invoices can be canceled
    if (currentStatus !== 'authorized') {
      appService.message({
        flag: 'warning',
        text: 'only_authorized_can_be_canceled',
        duration: 3,
      });
      return;
    }

    // Open cancel modal
    showCancelNfceModal({
      company: storedCompany,
      invoiceId,
      invoiceNumber,
      isNfce: data?.documentType === 'nfce' || data?.documentType === 'NFCe',
      onSuccess: () => {
        if (typeof refresh === 'function') {
          refresh();
        }
      },
    });
  }, [data?.documentType, data?._id, data?.documentNumber, data?.status, data?.accessKey, storedCompany, refresh]);

  const handleComplementary = useCallback(async () => {
    modal.confirm({
      title: translateX('invoice_complementary_title'),
      content: translateX('invoice_complementary_confirm'),
      okText: translateX('confirm'),
      cancelText: translateX('cancel'),
      onOk: async () => {
        try {
          const msg = await appService.message({
            flag: 'loading',
            text: 'creating_complementary_invoice',
            duration: 0,
          });

          const [newInvoiceData] = await axiosService.post({
            url: API.INVOICES.URL.replace(':_id?', data._id) + '/complementary',
          });

          // Redirect to the newly created complementary invoice
          const newInvoiceId = newInvoiceData?._id;

          if (newInvoiceId) {
            msg.update({
              flag: 'success',
              text: 'complementary_invoice_created',
              duration: 3,
            });
            // Derive base path from current URL to support any app/route configuration
            // e.g. /app/fiscal/invoices/699dfe09... → /app/fiscal/invoices
            const basePath = window.location.pathname.replace(/\/[^/]+$/, '');
            navigate(`${basePath}/${newInvoiceId}?mode=edit`);
          } else {
            msg.update({
              flag: 'error',
              text: 'unknown_error',
              duration: 3,
            });
          }
        } catch (error) {
          appService.notification({
            flag: 'error',
            title: 'error_creating_complementary',
            description: error.message,
            noTranslateDescription: true,
          });
        }
      },
    });
  }, [data?._id, navigate, modal]);

  const handleDevolutionInvoice = useCallback(async () => {
    // NFC-e → creates NF-e de Ajuste (finNFe=3) via /reversal endpoint
    // NF-e  → creates NF-e de Devolucao (finNFe=4) via /devolution endpoint
    const isNfce = data?.documentType === 'nfce';

    const confirmTitle = isNfce
      ? translateX('invoice_reversal_title')
      : translateX('invoice_devolution_title');

    const confirmContent = isNfce
      ? translateX('invoice_reversal_confirm')
      : translateX('invoice_devolution_confirm');

    modal.confirm({
      title: confirmTitle,
      content: confirmContent,
      okText: translateX('confirm'),
      cancelText: translateX('cancel'),
      onOk: async () => {
        try {
          const loadingText = isNfce ? 'creating_reversal_invoice' : 'creating_devolution_invoice';
          const msg = await appService.message({
            flag: 'loading',
            text: loadingText,
            duration: 0,
          });

          // Choose endpoint based on document type
          const endpoint = isNfce
            ? API.INVOICES.REVERSAL.replace(':_id', data._id)
            : API.INVOICES.DEVOLUTION.replace(':_id', data._id);

          const [newInvoiceData] = await axiosService.post({ url: endpoint });

          const newInvoiceId = newInvoiceData?._id;

          if (newInvoiceId) {
            const successText = isNfce ? 'reversal_invoice_created' : 'devolution_invoice_created';
            msg.update({ flag: 'success', text: successText, duration: 3 });

            // Derive base path from current URL to support any app/route configuration
            // e.g. /app/fiscal/invoices/699dfe09... → /app/fiscal/invoices
            const basePath = window.location.pathname.replace(/\/[^/]+$/, '');
            navigate(`${basePath}/${newInvoiceId}?mode=edit`);
          } else {
            msg.update({ flag: 'error', text: 'unexpected_api_response', duration: 3 });
          }

        } catch (error) {
          appService.messageDestroy();
          const errorTitle = isNfce
            ? translateX('error_creating_reversal')
            : translateX('error_creating_devolution');
          appService.notification({
            flag: 'error',
            title: errorTitle,
            description: error?.response?.data?.message || error?.message,
            duration: 8,
          });
        }
      },
    });
  }, [data?._id, data?.documentType, navigate, modal]);

  const handleCorrectionLetter = useCallback(() => {
    const invoiceId = data?._id;
    const invoiceNumber = data?.documentNumber;
    const currentStatus = data?.status;

    // Only authorized or corrected invoices can receive correction letters
    if (!['authorized', 'corrected'].includes(currentStatus)) {
      appService.message({
        flag: 'warning',
        text: 'only_authorized_or_corrected_can_receive_cce',
        duration: 3,
      });
      return;
    }

    // Open correction letter modal
    showCorrectionLetterModal({
      company: storedCompany,
      invoiceId,
      invoiceNumber,
      isNfce: data?.documentType === 'nfce' || data?.documentType === 'NFCe',
      onSuccess: () => {
        if (typeof refresh === 'function') {
          refresh();
        }
      },
    });
  }, [data?.documentType, data?._id, data?.documentNumber, data?.status, storedCompany, refresh]);

  const handleComplementaryInvoice = useCallback(() => {
    appService.message({ flag: 'info', text: 'complementary_invoice_clicked' });
  }, [data?._id, data?.accessKey]);

  const handlePrintDocument = useCallback(() => {
    // Open preview modal with print options
    showPreviewNFCeModal({
      data,
    });
  }, [data]);

  const handleValidateInvoice = useCallback(async () => {
    const invoiceId = data?._id;
    const documentType = data?.documentType?.toUpperCase() || 'NFE';

    // ─────────────────────────────────────────────────────────────────────────
    // STEP 0: VALIDATE COMPANY ELIGIBILITY
    // ─────────────────────────────────────────────────────────────────────────
    const validation = await invoicesService.validateEmissionEligibility({
      invoice: data,
    });

    if (!validation.valid) {
      // Notification already shown by validateEmissionEligibility
      return;
    }

    // ─────────────────────────────────────────────────────────────────────────
    // SINGLE MESSAGE - will be updated throughout the process
    // ─────────────────────────────────────────────────────────────────────────
    const msg = await appService.message({
      flag: 'loading',
      text: 'validating_fiscal_document_wait',
      duration: 0, // Keep visible until we update it
    });

    try {
      // ─────────────────────────────────────────────────────────────────────────
      // PROCESS 1: SAVE THE DOCUMENT SILENTLY
      // ─────────────────────────────────────────────────────────────────────────
      try {
        // Validate and get form values
        const allValues = await form.validateFields();

        // Use form value, then data, then defaultDocumentType (based on subscription)
        const docType = allValues?.documentType || data?.documentType || defaultDocumentType;
        const isNfce = docType === 'nfce';

        // Get recipientId from form
        const recipientId = allValues?.recipientId || data?.recipientId;

        // Validate items
        if (!items || items.length === 0) {
          msg.update({ flag: 'error', text: 'invoice_validation_no_items', duration: 4 });
          return;
        }

        // Validate recipient for NFe
        if (!isNfce && !recipientId) {
          msg.update({ flag: 'error', text: 'invoice_validation_no_recipient', duration: 4 });
          return;
        }

        // Return, complementary and adjustment (storno) invoices do not have their own payment
        // (the payment belongs to the referenced original invoice)
        // Use form value first (allValues) to handle unsaved changes, fallback to data
        const isReturnOrComplementary = ['return', 'complementary', 'adjustment'].includes(
          allValues?.purpose || data?.purpose
        );

        // Validate payments (skip for return/complementary)
        if (!isReturnOrComplementary && (!payments || payments.length === 0)) {
          msg.update({ flag: 'error', text: 'invoice_validation_no_payments', duration: 4 });
          return;
        }

        // Calculate totals
        const invoiceTotals = financialService.calculateInvoiceTotals(items);
        const taxTotals = fiscalService.calculateInvoiceTaxes(items);
        const paymentsTotal = financialService.roundCurrency(
          payments.reduce((sum, p) => sum + (p.amount || 0), 0)
        );
        // Calculate change (troco) - positive when payments exceed invoice total
        const changeValue = financialService.calculateChange(
          invoiceTotals.invoiceTotal,
          paymentsTotal
        );

        // Validate payment amount (skip for return/complementary)
        if (!isReturnOrComplementary && paymentsTotal < financialService.roundCurrency(invoiceTotals.invoiceTotal)) {
          msg.update({
            flag: 'error',
            text: 'invoice_validation_insufficient_payment',
            duration: 4,
          });
          return;
        }

        // Get serie from company fiscal data
        const serie = isNfce
          ? storedCompany?.fiscalData?.nfce?.serie || 1
          : storedCompany?.fiscalData?.nfe?.serie || 1;

        const environment =
          allValues?.environment ??
          (isNfce
            ? storedCompany?.fiscalData?.nfce?.environment
            : storedCompany?.fiscalData?.nfe?.environment) ??
          storedCompany?.fiscalData?.defaultEnvironment ??
          'homologation';

        // Build data to save
        const dataToSave = {
          ...allValues,
          documentType: docType,
          documentSerie: serie,
          environment,
          // Preserve existing DB value as fallback to avoid overwriting estorno/return/complementary natOp
          operationNature: allValues?.natureOfOperation || data?.operationNature || 'Venda',
          isFinalConsumer: allValues?.finalConsumer === true,
          issuer: buildIssuerData({ issuer, storedCompany, data }),
          // Deep merge: preserve fields without Form.Item (e.g. address) from saved data
          recipient: { ...(data?.recipient || {}), ...(allValues?.recipient || {}) },
          recipientId,
          items: cleanItemsForBackend(items),
          totals: {
            productsAmount: invoiceTotals.productsTotal,
            servicesAmount: 0,
            discountAmount: invoiceTotals.discount,
            freightAmount: invoiceTotals.freight,
            insuranceAmount: invoiceTotals.insurance,
            otherExpensesAmount: invoiceTotals.otherExpenses,
            icmsBaseAmount: taxTotals.icmsBase,
            icmsAmount: taxTotals.icmsValue,
            pisAmount: taxTotals.pisValue,
            cofinsAmount: taxTotals.cofinsValue,
            ipiAmount: taxTotals.ipiValue,
            documentAmount: invoiceTotals.invoiceTotal,
          },
          payment: {
            indicator: allValues?.payment?.indicator ?? 0,
            methods: payments.map(p => ({
              code: p.code,
              type: p.type,
              amount: financialService.roundCurrency(p.amount || 0),
              description: p.description || '',
            })),
            // Change (troco) - SEFAZ requires this when payment exceeds document total
            change: changeValue > 0 ? financialService.roundCurrency(changeValue) : 0,
          },
          logistics: {
            ...(allValues?.logistics || {}),
            freightModeCode: allValues?.freightModality ?? 'noFreight',
            carrier: allValues?.carrier || data?.logistics?.carrier,
          },
        };

        delete dataToSave.carrierId;
        delete dataToSave.carrier;

        // Save the document silently (using axiosService.put directly to avoid upsert messages)
        await axiosService.put({
          url: API.INVOICES.URL.replace(':_id?', invoiceId),
          data: dataToSave,
        });
      } catch (saveError) {
        // If it's a form validation error, scroll to the first failing field and show specific message
        if (saveError?.errorFields?.length > 0) {
          const firstErrorField = saveError.errorFields[0]?.name;
          if (firstErrorField) form.scrollToField(firstErrorField, { behavior: 'smooth', block: 'center' });
          msg.update({ flag: 'error', text: 'invoice_validation_fill_required_fields', duration: 5 });
        } else {
          msg.update({ flag: 'error', text: 'save_failed', duration: 4 });
        }

        return;
      }

      // Small delay to ensure save is reflected
      await new Promise(resolve => setTimeout(resolve, 300));

      // ─────────────────────────────────────────────────────────────────────────
      // PROCESS 2: VALIDATE THE DOCUMENT
      // ─────────────────────────────────────────────────────────────────────────
      const response = await axiosService.post({
        url: API.INVOICES.VALIDATE,
        data: {
          ids: [invoiceId],
          level: 'internal',
          updateDocuments: true,
        },
      });

      // Response is an array: [responseData, metadata]
      const [responseData] = response || [];
      const detail = responseData?.details?.[0];

      if (detail?.isValid) {
        // Validation passed - update message to success
        msg.update({
          flag: 'success',
          text: 'invoice_validated_successfully',
          duration: 3,
        });
      } else {
        // Validation failed - update message to error
        msg.update({
          flag: 'error',
          text: 'validation_failed',
          duration: 4,
        });
      }

      // Refresh document to show updated validation status and Alert with errors
      if (typeof refresh === 'function') {
        refresh({ _id: invoiceId });
      }
    } catch (error) {
      const errorData = error?.response?.data?.data || error?.response?.data || {};
      const errorMessage = errorData?.message || error?.message || translateX('unknown_error');

      // Update message to error
      msg.update({
        flag: 'error',
        text: 'validation_failed',
        duration: 4,
      });

      // Show notification with detailed error
      appService.notification({
        flag: 'error',
        title: 'invoice_validation_error',
        description: errorMessage,
      });

      // Refresh document to show updated status
      if (typeof refresh === 'function') {
        refresh({ _id: invoiceId });
      }
    }
  }, [
    data?._id,
    data?.documentType,
    data?.recipientId,
    data?.recipient,
    defaultDocumentType,
    invoiceStatus,
    refresh,
    form,
    items,
    payments,
    storedCompany,
    issuer,
  ]);

  // Build tabs after handlers are defined
  useEffect(() => {
    const tabsLocal = buildTabsArray({
      items,
      setItems,
      payments,
      setPayments,
      storedCompany,
      viewMode,
      issuer,
      isAuthorized,
      // Handlers for PreviewTab actions
      onOpenEmailModal: handleOpenEmailModal,
      onOpenWhatsappModal: handleOpenWhatsappModal,
      onPrint: handlePrintDocument,
    });
    buildTabs(tabsLocal);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    items,
    payments,
    viewMode,
    storedLanguage,
    issuer,
    isAuthorized,
    handleOpenEmailModal,
    handleOpenWhatsappModal,
    handlePrintDocument,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // For draft complementary/devolution/adjustment invoices (not yet emitted),
  // always resolve environment and issueDate from the company's current fiscal
  // configuration — never inherit these fields from backend-stored data, which
  // may carry a defaultEnvironment: 'homologation' or a mock issueDate (e.g. 2011)
  // copied from an original homologation emission.
  const isDraftReferenceInvoice =
    data?.status === 'draft' &&
    !data?.accessKey &&
    ['complementary', 'return', 'adjustment'].includes(data?.purpose);

  // Environment: always default to 'production' for new and reference invoice drafts.
  // IMPORTANT: fiscalData.nfe.environment and fiscalData.defaultEnvironment ALWAYS equal
  // 'homologation' in MongoDB (it is the Mongoose schema default, never null/undefined).
  // Using these values as the form default would always show 'homologation', causing wrong
  // emissions. Instead: for draft reference invoices (estorno, complementary, devolution),
  // always default to 'production' — the user can change via the dropdown if needed.
  const resolvedEnvironment = (() => {
    if (!isDraftReferenceInvoice) return data?.environment ?? 'production';
    // Reference invoice drafts: always default to 'production'.
    // These follow up authorized invoices; inheriting 'homologation' from schema defaults
    // or from the cloned original would silently cause AAMM=1109 in the access key.
    return 'production';
  })();

  // IssueDate: for draft reference invoices, reset to current date if the stored
  // value is from a clearly wrong year (e.g. 2011 mock date from a homologation
  // emission that was copied to the estorno/complementary/devolution by the backend).
  const resolvedIssueDate = (() => {
    const stored = data?.issueDate;
    if (!stored) return new Date();
    if (isDraftReferenceInvoice) {
      const storedYear = new Date(stored).getFullYear();
      const currentYear = new Date().getFullYear();
      if (storedYear < currentYear - 1) return new Date();
    }
    return stored;
  })();

  const initialValues = {
    ...(data || {}),
    // Default values for new invoice - document type based on subscription resources
    documentType: data?.documentType || defaultDocumentType,
    operationType: data?.operationType ?? 'exit',
    finalConsumer: data?.finalConsumer ?? true,
    environment: resolvedEnvironment,
    consumerPresence: data?.consumerPresence ?? 'inPerson',
    purpose: data?.purpose ?? 'normal',
    natureOfOperation: data?.natureOfOperation || data?.operationNature || 'Venda',
    issueDate: resolvedIssueDate,
    intermediation: data?.intermediation ?? 'own',
    freightModality: data?.freightModality ?? data?.logistics?.freightModeCode ?? 'noFreight',
    // Payment defaults
    payment: data?.payment || {
      indicator: 0, // Cash payment
    },
  };

  const tabsItems =
    availableTabs?.map(t => ({
      key: t.key,
      label: t.label,
      children: t.children,
      forceRender: t.forceRender,
    })) || [];

  // Build float actions array based on invoice status (see actions/floatButton.js)
  const floatActions = invoicesDocumentFloatButton({
    data,
    issuer,
    isEmitting,
    handlers: {
      onValidate: handleValidateInvoice,
      onEmit: handleEmitInvoice,
      onDownloadPdf: handleDownloadPdf,
      onDownloadXml: handleDownloadXml,
      onSendEmail: handleOpenEmailModal,
      onSendWhatsapp: handleOpenWhatsappModal,
      onCancel: handleCancelInvoice,
      onCorrectionLetter: handleCorrectionLetter,
      onComplementary: handleComplementary,
      onDevolution: handleDevolutionInvoice,
      onPrint: handlePrintDocument,
    },
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <div className="container mt-3">
        <SimpleFormProvider
          formRef={formRef}
          onFinish={handleFinish}
          initialValues={initialValues}
          requiredFields={requiredFields}
          locked={viewMode === 'view' || isEmitting}
          showSubmitButton={false}
          showInfoMessage={false}
          data={data}
        >
          <SimpleFormProviderInitializer data={data} isInitializedRef={isInitializedRef} />

          <div className="position-relative">
            <CardHeaderWithCountry
              overlapsBackground
              data={data}
              avatarName={data?.recipientName || data?.recipient?.name}
              watchField="documentType"
            />

            <SimpleLoadingArea
              name="cardHeader"
              offsetTop={0}
              showProgress={false}
              zIndex={100}
              borderRadius={8}
            />
          </div>

          <Tabs
            type="line"
            tabPlacement="top"
            activeKey={tab}
            rootClassName="tab-in-form no-content-padding"
            items={tabsItems}
            onChange={handleTabChange}
            animated={{ inkBar: true, tabPane: false }}
            destroyOnHidden={false}
          />
        </SimpleFormProvider>
      </div>

      {/* Float action button for invoice operations */}
      {floatActions.length > 0 && <ActionsFloatButton floatActions={floatActions} />}

      {/* Email Modal */}
      <Modal
        title={translateX('send_invoice_by_email')}
        open={emailModalOpen}
        onOk={handleSendEmail}
        onCancel={() => setEmailModalOpen(false)}
        confirmLoading={sendingEmail}
        okText={translateX('send')}
        cancelText={translateX('cancel')}
      >
        <Form form={emailForm} layout="vertical">
          <Form.Item
            name="to"
            label={translateX('recipient_email')}
            rules={[
              { required: true, message: translateX('email_required') },
              { type: 'email', message: translateX('email_invalid') },
            ]}
          >
            <Input placeholder="email@exemplo.com" />
          </Form.Item>

          <Form.Item name="subject" label={translateX('subject')}>
            <Input />
          </Form.Item>

          <Form.Item name="message" label={translateX('message')}>
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      {/* WhatsApp Modal */}
      <Modal
        title={translateX('send_invoice_by_whatsapp')}
        open={whatsappModalOpen}
        onOk={handleSendWhatsapp}
        onCancel={() => setWhatsappModalOpen(false)}
        confirmLoading={sendingWhatsapp}
        okText={translateX('send')}
        cancelText={translateX('cancel')}
      >
        <Form form={whatsappForm} layout="vertical">
          <Form.Item
            name={['phone']}
            label={translateX('phone_number')}
            rules={[{ required: true, message: translateX('phone_required') }]}
          >
            <Input placeholder="(11) 99999-9999" />
          </Form.Item>

          <Form.Item name="message" label={translateX('message')}>
            <Input.TextArea rows={4} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Fiscal Bootstrap Modal - Shows when company needs fiscal configuration */}
      <FiscalBootstrapModal
        show={showFiscalBootstrap}
        setShow={setShowFiscalBootstrap}
        company={storedCompany}
        subscription={storedSubscription}
        afterClose={() => {
          // Refresh document after modal closes to get updated data
          refresh?.();
        }}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default InvoicesDocument;

// *******************************************************************************
// HELPERS
// *******************************************************************************

function buildTabsArray(props) {
  const { isAuthorized } = props;

  const tabs = [
    {
      key: 'record',
      label: <span>{translateX('invoice_data')}</span>,
      children: <InvoicesRecordTab {...props} />,
    },
  ];

  // Only show preview and attachments tabs after document is authorized or canceled
  if (isAuthorized) {
    tabs.push(
      // {
      //   key: 'preview',
      //   label: <span>{translateX('preview')}</span>,
      //   children: <PreviewTab {...props} />,
      // },
      {
        key: 'attachments',
        label: <span>{translateX('attachments')}</span>,
        children: <AttachmentsTab {...props} />,
      }
    );
  }

  return tabs;
}

function convertTaxRegimeToCrt(taxRegime) {
  // If already a number, return it
  if (typeof taxRegime === 'number') {
    return taxRegime;
  }

  // Convert string to CRT code
  if (typeof taxRegime === 'string') {
    const regime = taxRegime.toLowerCase();
    if (regime.includes('mei')) return 4;
    if (regime.includes('simples') && regime.includes('excess')) return 2;
    if (regime.includes('simples')) return 1;
    if (regime.includes('normal') || regime.includes('lucro')) return 3;
  }

  // Default to Simples Nacional if unknown
  return 1;
}

function buildIssuerData({ issuer, storedCompany, data }) {
  // Always rebuild from source to ensure all fields are present
  // Priority: issuerEntity (editing/backend hydrated) > storedCompany (new invoice)
  const source = data?.issuerEntity || storedCompany;

  if (!source) {
    // Fallback to incomplete issuer if no source available
    return issuer || { entityId: null };
  }

  // Extract CNPJ value (can be object or string)
  const cnpjValue = typeof source.cnpj === 'object' ? source.cnpj?.value : source.cnpj;

  // Extract IE (can be in ie field or fiscalData.stateRegistration)
  const ieValue = source.ie || source.fiscalData?.stateRegistration;

  // Extract taxRegime (string) and convert to CRT code (number)
  const taxRegimeSource = source.fiscalData?.taxRegime;
  const taxRegimeCode = convertTaxRegimeToCrt(taxRegimeSource);

  // Build address object aligned with shared/addressSchema pattern
  const sourceAddress = source.address || {};
  const addressData = {
    zip: sourceAddress.zip || sourceAddress.zipCode || sourceAddress.postalCode,
    address1: sourceAddress.address1 || sourceAddress.street,
    address2: sourceAddress.address2 || sourceAddress.complement,
    number: sourceAddress.number || 'S/N',
    neighborhood: sourceAddress.neighborhood || sourceAddress.district,
    city: sourceAddress.city,
    cityCode: sourceAddress.cityCode,
    region: sourceAddress.region || sourceAddress.state,
  };

  // Return object with field names aligned with entities pattern
  return {
    entityId: source._id,
    // CNPJ number (aligned with entities documents[].value)
    cnpj: cnpjValue,
    // Legal name (aligned with entities name/fullName)
    name: source.razaoSocial || source.name,
    // Trade name
    tradeName: source.nomeFantasia,
    // IE - State Registration (aligned with fiscalData.stateRegistration)
    stateRegistration: ieValue,
    // Tax Regime (string enum aligned with fiscalData.taxRegime)
    taxRegime: taxRegimeSource || 'simples_nacional',
    // CRT code for SEFAZ (1=Simples, 2=Simples Excesso, 3=Normal)
    taxRegimeCode,
    // Full address for XML generation
    address: addressData,
  };
}

function cleanItemsForBackend(items) {
  return items.map((item, index) => {
    // Destructure to remove frontend-only fields
    const { discount, ...cleanItem } = item;

    // Ensure discountValue and discountPercentage are set from discount object if needed
    const discountValue = item.discountValue ?? discount?.value ?? 0;
    const discountPercentage = item.discountPercentage ?? discount?.percentage ?? 0;

    return {
      ...cleanItem,
      itemNumber: index + 1,
      discountValue: financialService.roundCurrency(discountValue),
      discountPercentage: financialService.roundCurrency(discountPercentage),
    };
  });
}
